import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { adminEndpoints } from "@/lib/adminApi";
import { AdminPageHeader, AdminCard, EmptyState } from "@/components/admin/AdminUI";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function AdminJobsPage() {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = () => {
        setLoading(true);
        adminEndpoints
            .jobs()
            .then((d) => setJobs(d))
            .catch((err) => setError(err?.response?.data?.detail || "Failed to load jobs"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const handleDelete = async (job) => {
        if (
            !window.confirm(
                `Delete "${job.title}"? Applications linked to this job will remain in the database but will lose the job reference.`,
            )
        ) {
            return;
        }
        try {
            await adminEndpoints.deleteJob(job.id);
            toast.success("Job deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to delete job");
        }
    };

    return (
        <div data-testid="admin-jobs-page">
            <AdminPageHeader
                eyebrow="Careers"
                title="Job listings"
                description="Publish and manage job openings for the public /careers page. Sponsorship availability is surfaced to applicants."
                testId="admin-jobs-header"
                actions={
                    <Link
                        to="/admin/jobs/new"
                        className="inline-flex items-center gap-2 bg-brand-gold px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-brand-charcoal hover:-translate-y-0.5"
                        data-testid="admin-jobs-new-button"
                    >
                        <Plus className="h-4 w-4" />
                        New job
                    </Link>
                }
            />

            {error && (
                <p className="mb-6 border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            )}

            {loading ? (
                <AdminCard>Loading…</AdminCard>
            ) : jobs.length === 0 ? (
                <EmptyState
                    title="No jobs yet"
                    hint="Publish your first job to display it on /careers."
                    testId="admin-jobs-empty"
                />
            ) : (
                <div className="overflow-x-auto border border-white/10 bg-white/[0.02]">
                    <table className="min-w-full text-left text-sm" data-testid="admin-jobs-table">
                        <thead className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/60">
                            <tr>
                                <th className="px-4 py-3">Title</th>
                                <th className="px-4 py-3">Department</th>
                                <th className="px-4 py-3">Location</th>
                                <th className="px-4 py-3">Deadline</th>
                                <th className="px-4 py-3 text-center">Published</th>
                                <th className="px-4 py-3 text-center">Apps</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((j) => (
                                <tr
                                    key={j.id}
                                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]"
                                    data-testid={`admin-job-row-${j.id}`}
                                >
                                    <td className="px-4 py-3 font-body text-brand-offwhite">
                                        {j.title}
                                        {j.sponsorship_available && (
                                            <span className="ml-2 border border-brand-gold px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] text-brand-gold">
                                                Sponsor
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-body text-brand-offwhite/80">
                                        {j.department}
                                    </td>
                                    <td className="px-4 py-3 font-body text-brand-offwhite/80">{j.location}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/60">
                                        {formatDate(j.deadline)}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {j.published ? (
                                            <span className="border border-emerald-400 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                                                Live
                                            </span>
                                        ) : (
                                            <span className="border border-white/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/60">
                                                Draft
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center font-display text-xl leading-none text-brand-gold">
                                        {j.applications_count}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/admin/jobs/${j.id}/edit`)}
                                                className="grid h-8 w-8 place-items-center border border-white/20 hover:border-brand-gold hover:text-brand-gold"
                                                aria-label={`Edit ${j.title}`}
                                                data-testid={`admin-job-edit-${j.id}`}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(j)}
                                                className="grid h-8 w-8 place-items-center border border-white/20 hover:border-rose-400 hover:text-rose-400"
                                                aria-label={`Delete ${j.title}`}
                                                data-testid={`admin-job-delete-${j.id}`}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
